/**
 * PreviewModeToggle — switches an org-preview session between "View as org"
 * (faithful to the previewed org's flags) and "Work as parent" (adds
 * isSelfService so the parent can use the library and campaign builder on the
 * org's behalf). Only rendered when the REAL user has isSelfService; see
 * core/components/orgs/previewAccess.js.
 */

import React from 'react';
import { Segmented } from 'antd';
import { useMutation } from '@apollo/react-hooks';
import { usePermify } from '@permify/react-role';
import UPDATE_USER from '../../../core/GraphQl/Mutations/UPDATE_USER';
import {
  computePreviewAccess,
  PREVIEW_MODES,
} from '../../../core/components/orgs/previewAccess';

const OPTIONS = [
  { label: 'View as org', value: PREVIEW_MODES.FAITHFUL },
  { label: 'Work as parent', value: PREVIEW_MODES.WORK_AS_PARENT },
];

const PreviewModeToggle = ({ currentUser }) => {
  const [updateUser] = useMutation(UPDATE_USER);
  const { setUser: setPermifyUser } = usePermify();

  const realUserIsSelfService = !!currentUser?.defaultRole?.permission?.isSelfService;
  if (!currentUser?.isPreviewOrg || !realUserIsSelfService) return null;

  const mode = currentUser.previewMode || PREVIEW_MODES.WORK_AS_PARENT;

  const handleModeChange = (nextMode) => {
    const { permission, roles } = computePreviewAccess({
      previewConfig: currentUser.role?.org?.config,
      level: currentUser.role?.org?.level,
      previewMode: nextMode,
      realUserIsSelfService,
    });

    const updatedUser = { ...currentUser, permission, previewMode: nextMode };
    delete updatedUser.__typename;

    // Persist (mirrors assumeRole's QuotaExceeded guard)
    try {
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    } catch (err) {
      console.error('Failed to persist preview mode to localStorage:', err);
    }

    // @permify/react-role's setUser only writes localStorage.__permifyUser —
    // mounted HasAccess/isAuthorized gates never re-evaluate. A reload (same
    // pattern as entering/exiting preview) rebuilds everything from the
    // persisted session so every gate picks up the new mode.
    setPermifyUser({ id: currentUser.id, roles, permissions: ['default'] });
    updateUser({ variables: updatedUser });

    // Strip the status filter from the URL before reloading: it persists
    // across the reload otherwise, so a `?status=draft` selected in
    // work-as-parent would leak drafts into the faithful view (whose dropdown
    // deliberately doesn't offer Draft for non-self-service orgs).
    const params = new URLSearchParams(window.location.search);
    params.delete('status');
    const search = params.toString();
    window.location.href = window.location.pathname + (search ? `?${search}` : '');
  };

  return (
    <Segmented
      size="small"
      style={{ marginLeft: 10 }}
      options={OPTIONS}
      value={mode}
      onChange={handleModeChange}
    />
  );
};

export default PreviewModeToggle;
